import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import toast from "react-hot-toast";
import { useEffect, useState } from "react";
import Select from "react-select";
import VideoCheckbox from "./VideoCheckbox";
import LoadingSpinner from "../../loader/Loader";
import { useGetCategoriesQuery } from "../../../features/api/videoCategoriesApiSlice";
import { useGetWooProductsQuery } from "../../../features/api/wooApiSlice";
import { FileUploader } from "../../uploads/FileUploader";
import { customSelectStyles } from "../../../util/customSelectStyles";
import "./adminVideo.scss";
import "react-quill/dist/quill.snow.css";
import {
  useGetVideoByIdQuery,
  useUpdateVideoMutation,
} from "../../../features/api/videoApiSlice";
import { useNavigate, useParams } from "react-router-dom";

const EditVideo = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const { data, isLoading } = useGetVideoByIdQuery(id);
  const video = data?.video;
  const { data: categories, isLoading: categoriesLoading } =
    useGetCategoriesQuery();

  const [updateVideo, { isLoading: isUpdating }] = useUpdateVideoMutation();

  const [uploadedVideoUrls, setUploadedVideoUrls] = useState(null);

  const [filters, setFilters] = useState({ search: "" });

  const {
    data: products = [],
    isLoading: productsLoading,
    isFetching,
    isError,
    refetch,
  } = useGetWooProductsQuery(filters);

  useEffect(() => {
    refetch();
  }, [filters, refetch]);

  // ✅ preload existing video URLs
  useEffect(() => {
    if (video) {
      setUploadedVideoUrls({
        fullUrl: video.fullUrl,
        previewUrl: video.previewUrl,
        thumbUrl: video.thumbUrl,
        posterUrl: video.posterUrl,
        resources: video.resources || [],
      });
    }
  }, [video]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const categoryOptions = categories?.map((cat) => ({
    value: cat._id,
    label: cat.name,
  }));

  const validationSchema = Yup.object().shape({
    videoTitle: Yup.string().required("Video title is required"),
    videoDescription: Yup.string().required("Video description is required"),
    categories: Yup.array()
      .min(1, "At least one category is required")
      .required("Video category is required"),
    videoType: Yup.string().required("Video type is required"),
  });

  if (isLoading || categoriesLoading || !video) {
    return <LoadingSpinner />;
  }

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);

    try {
      const res = await updateVideo({
        id,
        formData: {
          ...values,
          ...uploadedVideoUrls,
          products: values.products,
        },
      }).unwrap();

      toast.success(res.message);
      navigate("/admin/videos");
    } catch (err) {
      toast.error(err?.data?.message || "Failed to update video");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="dashboard-wrapper">
      <div className="dashboard-top-section">
        <div className="dash-top-flex">
          <h1 className="dashboard-title">Edit Video</h1>
          <button onClick={() => navigate("/admin/videos")}>
            Back to Videos
          </button>
        </div>
      </div>

      <Formik
        enableReinitialize
        initialValues={{
          featured: video.featured || false,
          published: video.published || false,
          videoTitle: video.videoTitle || "",
          videoDescription: video.videoDescription || "",
          categories: video.categories || [],
          videoType: video.videoType || "",
          products: video.products || [],
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, values, setFieldValue }) => (
          <Form className="dashboard-form">
            {/* Title */}
            <div>
              <label>
                Title<span className="required-field">*</span>
              </label>
              <Field name="videoTitle" />
              <ErrorMessage
                name="videoTitle"
                component="div"
                className="error"
              />
            </div>

            {/* Featured */}
            <div className="form-controls">
              <VideoCheckbox
                label="Featured Video"
                checked={values.featured}
                onChange={(e) => setFieldValue("featured", e.target.checked)}
              />
            </div>

            {/* Published */}
            <div className="form-controls">
              <VideoCheckbox
                label="Publish Video"
                checked={values.published}
                onChange={(e) => setFieldValue("published", e.target.checked)}
              />
            </div>

            {/* Video Type */}
            <div className="form-controls">
              <label>
                Public or Private<span className="required-field">*</span>
              </label>
              <Field as="select" name="videoType">
                <option value="">Select video type</option>
                <option value="paid">Paid</option>
                <option value="free">Free</option>
              </Field>
              <ErrorMessage
                name="videoType"
                component="div"
                className="error"
              />
            </div>

            {/* Categories */}
            <div className="form-group">
              <label>Video Categories</label>
              <Select
                isMulti
                options={categoryOptions}
                styles={customSelectStyles}
                value={categoryOptions?.filter((opt) =>
                  values.categories.includes(opt.value)
                )}
                onChange={(opts) =>
                  setFieldValue(
                    "categories",
                    opts ? opts.map((o) => o.value) : []
                  )
                }
              />
            </div>

            {/* File Upload */}
            <div>
              <label>Upload / Replace Video</label>
              <FileUploader
                identifier={`video-${id}`}
                editingMode
                initialUrls={uploadedVideoUrls}
                onUpload={(urls) => {
                  setUploadedVideoUrls({
                    fullUrl: urls.videoUrl,
                    previewUrl: urls.previewUrl,
                    thumbUrl: urls.thumbUrl,
                    posterUrl: urls.posterUrl,
                    resources: urls.resources || [],
                  });
                }}
              />
            </div>

            {/* Description */}
            <div>
              <label>
                Description<span className="required-field">*</span>
              </label>
              <ReactQuill
                className="custom-quill-editor"
                value={values.videoDescription || ""}
                onChange={(val) => setFieldValue("videoDescription", val)}
              />
              <ErrorMessage
                name="videoDescription"
                component="div"
                className="error"
              />
            </div>

            {/* Product Filter */}
            <div className="product-selection-section">
              <label>Linked Products</label>
              <div className="product-filter-bar">
                <input
                  type="text"
                  name="search"
                  placeholder="Search products..."
                  value={filters.search}
                  onChange={handleFilterChange}
                />
              </div>
            </div>

            {/* Product Selector */}
            <div className="products-linked-area">
              {(productsLoading || isFetching) && <LoadingSpinner />}
              {isError && (
                <p className="error-message">Failed to load products</p>
              )}

              <div className="product-selector-grid">
                {products.map((product) => {
                  const selected = values.products.includes(
                    product.id.toString()
                  );

                  return (
                    <div
                      key={product.id}
                      className={`product-card ${selected ? "selected" : ""}`}
                      onClick={() =>
                        setFieldValue(
                          "products",
                          selected
                            ? values.products.filter(
                                (id) => id !== product.id.toString()
                              )
                            : [...values.products, product.id.toString()]
                        )
                      }
                    >
                      {product.images?.[0] && (
                        <img src={product.images[0].src} alt={product.name} />
                      )}
                      <div className="product-card-info">
                        <h4>{product.name}</h4>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting || isUpdating}
              className="form-button"
            >
              Update Video
            </button>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default EditVideo;
